import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { ProcessedOrder, EnhancedOrderBox, parseAddressForDisplay } from "../../types/logistics";

import { FaMapMarker, FaClock, FaBox, FaCheckCircle, FaWarehouse, FaExclamationTriangle, FaBan } from "react-icons/fa";

import './OrderCard.scss';

const FAILED_STATUSES = ['WCFC', 'ITTKKFR'];

type orderCardProps = {
    data: ProcessedOrder;
    dragRef?: React.RefObject<boolean>;
};

const OrderCard = (props: orderCardProps) => {
    const order = props.data;

    const navigate = useNavigate();

    // Determine if this is a collection or delivery stop
    const isCollection = order.type === 'collection';

    const addressString = isCollection ? order.collectionAddress : order.deliveryAddress;
    const address = parseAddressForDisplay(addressString);
    const eta = isCollection ? order.collectionEta : order.deliveryEta;
    // Get customer name
    const customerName = order.customer
        ? `${order.customer.firstName} ${order.customer.lastName}`.trim()
        : 'Unknown Customer';


    // Calculate box counts based on enhanced box data, excluding failed-status boxes
    const { totalBoxes, actionableBoxes, completedBoxes, awaitingLoadBoxes, failedBoxes } = useMemo(() => {
        const allBoxes = order.boxes || [];
        const failed = allBoxes.filter((b: EnhancedOrderBox) => FAILED_STATUSES.includes(b.statusCode)).length;
        const boxes = allBoxes.filter((b: EnhancedOrderBox) => !FAILED_STATUSES.includes(b.statusCode));
        const total = boxes.length;
        const actionable = boxes.filter((b: EnhancedOrderBox) => b.isActionable).length;
        const completed = boxes.filter((b: EnhancedOrderBox) => b.isCompleted).length;
        const awaitingLoad = boxes.filter((b: EnhancedOrderBox) => b.statusCode === 'WKKHO').length;
        return { totalBoxes: total, actionableBoxes: actionable, completedBoxes: completed, awaitingLoadBoxes: awaitingLoad, failedBoxes: failed };
    }, [order.boxes]);

    // Check if this is a delivery order with boxes awaiting load
    const isDelivery = order.type === 'delivery';
    const readiness = order.deliveryReadiness;
    const hasBoxesAwaitingLoad = isDelivery && awaitingLoadBoxes > 0;

    // Display box count: use allBoxCount for blocked deliveries so courier sees real total
    const displayBoxCount = isDelivery && readiness?.isBlocked && order.allBoxCount
        ? order.allBoxCount
        : totalBoxes + failedBoxes;

    // Determine if order is signed/completed based on:
    // 1. Local signature flag (immediate UI feedback after submission)
    // 2. Backend completion timestamps (after refresh/reload)
    const isOrderSigned = useMemo(() => {
        // Check local signature flag first (immediate feedback)
        if (order.signature) return true;

        // Check backend completion timestamps based on order type
        if (order.type === 'collection' && order.collectionCompletedAt) return true;
        if (order.type === 'delivery' && order.deliveryCompletedAt) return true;

        return false;
    }, [order.signature, order.type, order.collectionCompletedAt, order.deliveryCompletedAt]);

    // Check if proof photo already exists on the server for this phase
    const hasProofPhoto = isCollection
        ? !!order.collectionProofPhotoUrl
        : !!order.deliveryProofPhotoUrl;

    // Get full address string
    const getFullAddress = () => {
        if (!address) return addressString || 'No address';
        const parts = [
            address.address1,
            address.city,
            address.postcode
        ].filter(Boolean);
        return parts.join(', ');
    };

    // Get order status text based on box progress
    const getStatusText = () => {
        if (isOrderSigned) return 'Completed';
        // For blocked delivery orders, show readiness-driven status
        if (isDelivery && readiness?.isBlocked && readiness.cardMessage) {
            return readiness.cardMessage;
        }
        if (totalBoxes === 0 && failedBoxes > 0) return 'All Failed';
        if (completedBoxes === totalBoxes && totalBoxes > 0 && failedBoxes > 0) return hasProofPhoto ? 'Ready for Signature' : 'Needs Photo (partial)';
        if (completedBoxes === totalBoxes && totalBoxes > 0) return hasProofPhoto ? 'Ready for Signature' : 'Needs Photo & Signature';
        if (completedBoxes > 0) return `In Progress (${completedBoxes}/${totalBoxes})`;
        if (actionableBoxes > 0) return `${actionableBoxes} to process`;
        if (failedBoxes > 0) return `${failedBoxes} failed`;
        return 'Pending';
    };

    const openOrder = (event: React.MouseEvent<HTMLDivElement>) => {
        // If a drag just completed, suppress the click
        if (props.dragRef?.current) return;

        navigate(`/courier/order/${order.id}/${order.type}`);
    };

    const cardClass = () => {
        let classString = '';

        if(isOrderSigned) classString += ' ok';
        else if(isDelivery && readiness?.isBlocked && !readiness.hasLoadableBoxes) classString += ' blocked';
        else if(totalBoxes === 0 && failedBoxes > 0) classString += ' failed';
        else if(completedBoxes === totalBoxes && totalBoxes > 0) classString += ' ready';
        else if(completedBoxes > 0) classString += ' warn';
        else if(failedBoxes > 0) classString += ' warn';

        return classString;
    };

    return (
        <div onClick={openOrder} className={'card order-card' + cardClass()}>
            <div className="order-header">
                <span className="number storage">{order.index+1}</span>
                <span className={`order-type-badge ${isCollection ? 'collection' : 'delivery'}`}>
                    {isCollection ? 'COLLECTION' : 'DELIVERY'}
                </span>
                {isDelivery && readiness?.badgeText
                    ? <span className={`order-type-badge ${readiness.badgeClass}`}>{readiness.badgeText}</span>
                    : hasBoxesAwaitingLoad && <span className="order-type-badge awaiting-load">AWAITING LOAD</span>
                }
                {order.university && <span className="university-badge">{order.university}</span>}
                <span className="order-id">Order #{order.id}</span>
            </div>
            <div className="order-details">
                <div className="customer-name">{customerName}</div>
                <div className="address-line">
                    <FaMapMarker className="icon" />
                    <span>{getFullAddress()}</span>
                </div>
            </div>
            <div className="bottom">
                <span className="box-count">
                    <FaBox className="icon" />
                    <strong>{displayBoxCount}</strong> boxes
                    {completedBoxes > 0 && (
                        <span className="completed-indicator">
                            <FaCheckCircle className="icon-small" />
                            {completedBoxes}
                        </span>
                    )}
                    {failedBoxes > 0 && (
                        <span className="failed-indicator">
                            <FaExclamationTriangle className="icon-small" />
                            {failedBoxes} failed
                        </span>
                    )}
                </span>
                {isDelivery && readiness?.isBlocked && !readiness.hasLoadableBoxes && readiness.cardMessage && (
                    <span className="readiness-message">
                        <FaBan className="icon" />
                        {readiness.cardMessage}
                    </span>
                )}
                {!(isDelivery && readiness?.isBlocked && !readiness.hasLoadableBoxes) && order.pendingWarehouseBoxes != null && order.pendingWarehouseBoxes > 0 && (
                    <span className="pending-warehouse">
                        <FaWarehouse className="icon" />
                        +{order.pendingWarehouseBoxes} pending
                    </span>
                )}
                {eta && (
                    <span className="time-slot">
                        <FaClock className="icon" />
                        {eta}
                    </span>
                )}
                <span className={`status status-${isOrderSigned ? 'complete' : (isDelivery && readiness?.isBlocked && !readiness.hasLoadableBoxes ? 'blocked' : (totalBoxes === 0 && failedBoxes > 0 ? 'failed' : completedBoxes === totalBoxes && totalBoxes > 0 ? 'ready' : completedBoxes > 0 ? 'progress' : failedBoxes > 0 ? 'progress' : 'pending'))}`}>
                    {getStatusText()}
                </span>
            </div>
        </div>
    );
};

export default OrderCard;